import { useState } from "react";
import { Container } from "reactstrap";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router";

//Common
import { LoadingIndicator, Snackbar } from "../../../common/VFX-UI";

//App
import { queryKeys } from "./constants";
import { EMPTY_GUID } from "../../../../utility/constants/constants";
import { RoleClaimLookups, RoleClaimState } from "../../../../types/roleClaim";
import roleClaimApi from "../../../../app/model/api/roleClaimApi";
import RolePermissionsForm from "./RolePermissionsForm";
import { Options } from "../../../../types";
import { RoleClaimDto } from "../../../../app/model/api/types/dto/roleClaimDto";
import { apiErrorMessage } from "../../../../utility/lib/error";

const RolePermissionsFormContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState<RoleClaimState>({
    roleId: id ?? null,
    showSnackbar: false,
    successMessage: null,
    errorMessage: null,
  });

  const onShowSuccess = (message: string) => {
    setState((prevState) => ({
      ...prevState,
      showSnackbar: true,
      errorMessage: null,
      successMessage: message,
    }));
  };

  const onShowErrorSnackbar = (errorMessage: string) =>
    setState((prevState) => ({
      ...prevState,
      showSnackbar: true,
      successMessage: null,
      errorMessage: errorMessage,
    }));

  const onCloseSnackbar = () =>
    setState((prevState) => ({
      ...prevState,
      showSnackbar: false,
      successMessage: null,
      errorMessage: null,
    }));

  const fetchRolePermissionLookups = async (): Promise<RoleClaimLookups> => {
    const { result } = await roleClaimApi.getLookups();
    const { permissions, roles } = result;

    const permissionOptions: Options[] =
      permissions?.map((permission) => ({
        label: permission.display ?? "",
        value: permission.id ?? "",
      })) || [];

    const roleOptions: Options[] =
      roles?.map((role) => ({
        label: role.display ?? "",
        value: role.id ?? "",
      })) || [];

    return {
      permissions: permissionOptions,
      roles: roleOptions,
    };
  };

  const rolePermissionLookupQuery = useQuery({
    queryKey: [queryKeys.getRolePermissionLookups],
    queryFn: fetchRolePermissionLookups,
  });

  const rolePermissionQuery = useQuery({
    queryKey: [queryKeys.getRolePermissions, state.roleId],
    queryFn: () => roleClaimApi.get(state.roleId ?? EMPTY_GUID),
    enabled: state.roleId !== null,
  });

  const { status, mutate } = useMutation<void, Error, RoleClaimDto>({
    mutationFn: async (payload: RoleClaimDto) => {
      const response = await roleClaimApi.save(payload);
      const errorMessage = apiErrorMessage(response.errorMessages);
      if (errorMessage) {
        onShowErrorSnackbar(errorMessage);
      } else {
        onShowSuccess("Permissions saved.");
      }
    },
  });

  const updateRoleId = async (roleId: string | null) => {
    setState((prevState) => ({
      ...prevState,
      roleId: roleId,
    }));
  };

  const handleOnSubmit = async (values: RoleClaimDto) => {
    mutate(values);
  };

  const handleOnClose = () => navigate("/settings-page/role");

  const initialVal: RoleClaimDto = {
    roleId: state.roleId ? state.roleId : null,
    claimId: state.roleId
      ? rolePermissionQuery?.data?.result.claimId ?? []
      : [],
  };

  return rolePermissionQuery.isLoading ? (
    <LoadingIndicator />
  ) : (
    <Container>
      <RolePermissionsForm
        lookup={rolePermissionLookupQuery?.data}
        roleId={state.roleId}
        initialVal={initialVal}
        status={status}
        setRoleId={updateRoleId}
        handleOnSubmit={handleOnSubmit}
        onClose={handleOnClose}
      />
      {state.showSnackbar && (
        <Snackbar
          open={true}
          message={state.errorMessage ?? state.successMessage}
          handleClose={onCloseSnackbar}
          severity={state.errorMessage ? "danger" : "success"}
        />
      )}
    </Container>
  );
};

export default RolePermissionsFormContainer;
